<template>
  <transition name="image-viewer-fade" @after-leave="restorePageScroll">
    <section
      v-if="visible"
      ref="viewer"
      data-image-viewer
      class="image-viewer"
      :class="{ settling: imageAnimating || closingBySwipe }"
      tabindex="-1"
      role="dialog"
      aria-modal="true"
      :aria-label="$t('imageViewer.position', { current: $n(currentIndex + 1), total: $n(images.length) })"
      :style="viewerStyle"
      @click.self="closeFromBackdrop"
      @contextmenu.prevent
      @dblclick.stop.prevent="handleDoubleClick"
      @touchstart="handleTouchStart"
      @touchmove.prevent="handleTouchMove"
      @touchend="handleTouchEnd"
      @touchcancel="handleTouchEnd"
    >
      <header :style="chromeStyle">
        <button type="button" :aria-label="$t('imageViewer.close')" @click.stop="close">
          <i class="el-icon-close" />
        </button>
      </header>

      <div ref="viewport" class="image-viewer-viewport" :style="contentStyle" @click.self="closeFromBackdrop">
        <div class="image-viewer-strip" :class="{ animating: stripAnimating }" :style="stripStyle">
          <figure v-for="(source, index) in images" :key="`${source}-${index}`" @click="closeFromBackdrop">
            <img
              :ref="`image-${index}`"
              :src="source"
              :alt="$t('imageViewer.imageAlt', { index: $n(index + 1) })"
              :draggable="false"
              :class="{ current: index === currentIndex, animating: imageAnimating && index === currentIndex }"
              :style="index === currentIndex ? imageStyle : null"
              @load="handleImageLoad(index, $event)"
              @click.stop
              @contextmenu.prevent
            >
          </figure>
        </div>
      </div>

      <footer v-if="images.length > 1" :style="chromeStyle" aria-live="polite">
        {{ $t("imageViewer.position", { current: $n(currentIndex + 1), total: $n(images.length) }) }}
      </footer>
    </section>
  </transition>
</template>

<script>
import { imageViewerBus } from "../utils/imageViewer"

const MIN_SCALE = 1
const MAX_SCALE = 4
const DOUBLE_TAP_SCALE = 2
const SWIPE_THRESHOLD = 64
const DISMISS_THRESHOLD = 112

function distance(first, second) {
  return Math.hypot(second.clientX - first.clientX, second.clientY - first.clientY)
}

function midpoint(first, second) {
  return { x: (first.clientX + second.clientX) / 2, y: (first.clientY + second.clientY) / 2 }
}

export default {
  name: "ImageViewer",
  data() {
    return {
      visible: false,
      images: [],
      currentIndex: 0,
      scale: MIN_SCALE,
      translateX: 0,
      translateY: 0,
      swipeX: 0,
      dismissY: 0,
      backdropOpacity: 1,
      stripAnimating: false,
      imageAnimating: false,
      closingBySwipe: false,
      gesture: null,
      lastTap: { time: 0, x: 0, y: 0 },
      suppressBackdropUntil: 0,
      scrollSnapshot: null,
      animationTimer: 0
    }
  },
  computed: {
    viewerStyle() {
      return { backgroundColor: `rgba(0,0,0,${this.backdropOpacity})` }
    },
    chromeStyle() {
      return { opacity: Math.max(0, this.backdropOpacity * 1.08 - 0.08) }
    },
    contentStyle() {
      return { transform: `translate3d(0,${this.dismissY}px,0)` }
    },
    stripStyle() {
      return { transform: `translate3d(calc(${-this.currentIndex * 100}% + ${this.swipeX}px),0,0)` }
    },
    imageStyle() {
      return { transform: `translate3d(${this.translateX}px,${this.translateY}px,0) scale(${this.scale})` }
    }
  },
  mounted() {
    imageViewerBus.$on("image-viewer:open", this.open)
    window.addEventListener("keydown", this.handleKeydown)
  },
  beforeDestroy() {
    imageViewerBus.$off("image-viewer:open", this.open)
    window.removeEventListener("keydown", this.handleKeydown)
    window.clearTimeout(this.animationTimer)
    if (this.scrollSnapshot) this.restorePageScroll()
  },
  methods: {
    open(payload) {
      const sources = Array.isArray(payload && payload.images) ? payload.images.filter(Boolean) : []
      if (!sources.length) return
      this.images = sources
      this.currentIndex = Math.max(0, Math.min(sources.length - 1, Number(payload.index) || 0))
      this.resetImage(false)
      this.dismissY = 0
      this.backdropOpacity = 1
      this.closingBySwipe = false
      if (!this.visible) {
        this.lockPageScroll()
        this.visible = true
        this.$nextTick(() => this.$refs.viewer && this.$refs.viewer.focus && this.$refs.viewer.focus())
      }
    },
    close() {
      if (!this.visible) return
      this.imageAnimating = true
      this.visible = false
    },
    closeFromBackdrop() {
      if (Date.now() < this.suppressBackdropUntil) return
      this.close()
    },
    lockPageScroll() {
      if (this.scrollSnapshot) return
      const body = document.body
      const html = document.documentElement
      const scrollX = window.scrollX || window.pageXOffset || 0
      const scrollY = window.scrollY || window.pageYOffset || 0
      this.scrollSnapshot = {
        scrollX,
        scrollY,
        body: { position: body.style.position, top: body.style.top, left: body.style.left, width: body.style.width, overflow: body.style.overflow },
        htmlOverflow: html.style.overflow
      }
      document.body.style.position = "fixed"
      body.style.top = `${-scrollY}px`
      body.style.left = `${-scrollX}px`
      body.style.width = "100%"
      body.style.overflow = "hidden"
      html.style.overflow = "hidden"
    },
    restorePageScroll() {
      if (!this.scrollSnapshot) return
      const snapshot = this.scrollSnapshot
      const body = document.body
      const html = document.documentElement
      body.style.position = snapshot.body.position
      body.style.top = snapshot.body.top
      body.style.left = snapshot.body.left
      body.style.width = snapshot.body.width
      body.style.overflow = snapshot.body.overflow
      html.style.overflow = snapshot.htmlOverflow
      this.scrollSnapshot = null
      window.scrollTo(snapshot.scrollX, snapshot.scrollY)
    },
    handleTouchStart(event) {
      this.imageAnimating = false
      this.stripAnimating = false
      if (event.touches.length >= 2) {
        const center = midpoint(event.touches[0], event.touches[1])
        const viewport = this.viewportCenter()
        this.gesture = {
          mode: "pinch",
          startDistance: distance(event.touches[0], event.touches[1]),
          startScale: this.scale,
          contentX: (center.x - viewport.x - this.translateX) / this.scale,
          contentY: (center.y - viewport.y - this.translateY) / this.scale
        }
        return
      }
      const touch = event.touches[0]
      if (!touch) return
      this.gesture = {
        mode: this.scale > MIN_SCALE ? "pan" : "pending",
        startX: touch.clientX,
        startY: touch.clientY,
        startTranslateX: this.translateX,
        startTranslateY: this.translateY,
        startedAt: performance.now(),
        moved: false
      }
    },
    handleTouchMove(event) {
      if (!this.gesture) return
      if (event.touches.length >= 2) {
        if (this.gesture.mode !== "pinch") this.handleTouchStart(event)
        const center = midpoint(event.touches[0], event.touches[1])
        const viewport = this.viewportCenter()
        const nextScale = this.clamp(this.gesture.startScale * distance(event.touches[0], event.touches[1]) / Math.max(1, this.gesture.startDistance), MIN_SCALE, MAX_SCALE)
        this.scale = nextScale
        this.translateX = center.x - viewport.x - this.gesture.contentX * nextScale
        this.translateY = center.y - viewport.y - this.gesture.contentY * nextScale
        this.clampTranslation()
        return
      }
      const touch = event.touches[0]
      if (!touch || this.gesture.mode === "pinch") return
      const deltaX = touch.clientX - this.gesture.startX
      const deltaY = touch.clientY - this.gesture.startY
      if (Math.abs(deltaX) > 4 || Math.abs(deltaY) > 4) this.gesture.moved = true

      if (this.scale > MIN_SCALE || this.gesture.mode === "pan") {
        this.gesture.mode = "pan"
        this.translateX = this.gesture.startTranslateX + deltaX
        this.translateY = this.gesture.startTranslateY + deltaY
        this.clampTranslation()
        return
      }
      if (this.gesture.mode === "pending" && Math.max(Math.abs(deltaX), Math.abs(deltaY)) > 7) {
        this.gesture.mode = Math.abs(deltaX) > Math.abs(deltaY) ? "swipe" : "dismiss"
      }
      if (this.gesture.mode === "swipe") {
        const atStart = this.currentIndex === 0 && deltaX > 0
        const atEnd = this.currentIndex === this.images.length - 1 && deltaX < 0
        this.swipeX = atStart || atEnd ? deltaX * 0.24 : deltaX
      } else if (this.gesture.mode === "dismiss") {
        this.dismissY = deltaY > 0 ? deltaY : deltaY * 0.12
        this.backdropOpacity = this.clamp(1 - Math.max(0, deltaY) / Math.max(280, window.innerHeight) * 1.45, 0.22, 1)
      }
    },
    handleTouchEnd(event) {
      this.suppressBackdropUntil = Date.now() + 360
      if (event.touches && event.touches.length === 1) {
        const touch = event.touches[0]
        this.gesture = { mode: this.scale > MIN_SCALE ? "pan" : "pending", startX: touch.clientX, startY: touch.clientY, startTranslateX: this.translateX, startTranslateY: this.translateY, startedAt: performance.now(), moved: false }
        return
      }
      const gesture = this.gesture
      this.gesture = null
      if (!gesture) return
      if (gesture.mode === "pinch" || gesture.mode === "pan") {
        if (this.scale <= 1.02) this.resetImage(true)
        else this.animateImageClamp()
        return
      }
      const changed = event.changedTouches && event.changedTouches[0]
      const deltaX = changed ? changed.clientX - gesture.startX : this.swipeX
      const deltaY = changed ? changed.clientY - gesture.startY : this.dismissY
      const elapsed = Math.max(1, performance.now() - gesture.startedAt)
      if (gesture.mode === "swipe") {
        const direction = deltaX < 0 ? 1 : -1
        if ((Math.abs(deltaX) >= SWIPE_THRESHOLD || Math.abs(deltaX) / elapsed > 0.48) && this.canMove(direction)) this.moveBy(direction)
        else this.animateStripHome()
        return
      }
      if (gesture.mode === "dismiss") {
        if (deltaY >= DISMISS_THRESHOLD || deltaY / elapsed > 0.56) {
          this.closingBySwipe = true
          this.dismissY = Math.max(window.innerHeight, deltaY)
          this.backdropOpacity = 0
          window.clearTimeout(this.animationTimer)
          this.animationTimer = window.setTimeout(this.close, 180)
        } else {
          this.imageAnimating = true
          this.dismissY = 0
          this.backdropOpacity = 1
          this.clearAnimationState()
        }
        return
      }
      if (!gesture.moved && changed) this.handleTap(changed.clientX, changed.clientY)
    },
    handleTap(x, y) {
      const now = Date.now()
      if (now - this.lastTap.time < 300 && Math.hypot(x - this.lastTap.x, y - this.lastTap.y) < 34) {
        this.lastTap.time = 0
        this.toggleDoubleTapZoom(x, y)
      } else {
        this.lastTap = { time: now, x, y }
      }
    },
    handleDoubleClick(event) {
      this.toggleDoubleTapZoom(event.clientX, event.clientY)
    },
    toggleDoubleTapZoom(x, y) {
      if (this.scale > MIN_SCALE) {
        this.resetImage(true)
        return
      }
      const viewport = this.viewportCenter()
      this.scale = DOUBLE_TAP_SCALE
      this.translateX = -(x - viewport.x) * (DOUBLE_TAP_SCALE - 1)
      this.translateY = -(y - viewport.y) * (DOUBLE_TAP_SCALE - 1)
      this.animateImageClamp()
    },
    moveBy(offset) {
      if (this.scale > MIN_SCALE || !this.canMove(offset)) return
      this.stripAnimating = true
      this.currentIndex += offset
      this.swipeX = 0
      this.resetImage(false)
      this.clearAnimationState()
    },
    canMove(offset) {
      const next = this.currentIndex + offset
      return next >= 0 && next < this.images.length
    },
    animateStripHome() {
      this.stripAnimating = true
      this.swipeX = 0
      this.clearAnimationState()
    },
    animateImageClamp() {
      this.imageAnimating = true
      this.clampTranslation()
      this.clearAnimationState()
    },
    clearAnimationState() {
      window.clearTimeout(this.animationTimer)
      this.animationTimer = window.setTimeout(() => { this.stripAnimating = false; this.imageAnimating = false }, 240)
    },
    resetImage(animate = false) {
      this.scale = MIN_SCALE
      this.translateX = 0
      this.translateY = 0
      this.swipeX = 0
      this.imageAnimating = animate
      if (animate) this.clearAnimationState()
    },
    clampTranslation() {
      if (this.scale <= MIN_SCALE) {
        this.translateX = 0
        this.translateY = 0
        return
      }
      const viewport = this.$refs.viewport
      const image = this.currentImageElement()
      if (!viewport || !image || !image.naturalWidth || !image.naturalHeight) return
      const ratio = Math.min(viewport.clientWidth / image.naturalWidth, viewport.clientHeight / image.naturalHeight)
      const baseWidth = image.naturalWidth * ratio
      const baseHeight = image.naturalHeight * ratio
      const maxX = Math.max(0, (baseWidth * this.scale - viewport.clientWidth) / 2)
      const maxY = Math.max(0, (baseHeight * this.scale - viewport.clientHeight) / 2)
      this.translateX = this.clamp(this.translateX, -maxX, maxX)
      this.translateY = this.clamp(this.translateY, -maxY, maxY)
    },
    currentImageElement() {
      const entry = this.$refs[`image-${this.currentIndex}`]
      return Array.isArray(entry) ? entry[0] : entry
    },
    viewportCenter() {
      const viewport = this.$refs.viewport
      if (!viewport) return { x: window.innerWidth / 2, y: window.innerHeight / 2 }
      const bounds = viewport.getBoundingClientRect()
      return { x: bounds.left + bounds.width / 2, y: bounds.top + bounds.height / 2 }
    },
    handleImageLoad(index) {
      if (index === this.currentIndex) this.clampTranslation()
    },
    handleKeydown(event) {
      if (!this.visible) return
      if (event.key === "Escape") this.close()
      else if (event.key === "ArrowLeft") this.moveBy(-1)
      else if (event.key === "ArrowRight") this.moveBy(1)
    },
    clamp(value, minimum, maximum) {
      return Math.min(maximum, Math.max(minimum, value))
    }
  }
}
</script>

<style scoped>
.image-viewer{position:fixed;z-index:10020;inset:0;overflow:hidden;color:#fff;outline:0;touch-action:none;overscroll-behavior:none;-webkit-user-select:none;user-select:none;-webkit-touch-callout:none}
.image-viewer.settling{transition:background-color .22s ease}
.image-viewer>header{position:absolute;z-index:4;top:0;inset-inline:0;display:flex;justify-content:flex-end;padding:calc(env(safe-area-inset-top) + 10px) max(14px,env(safe-area-inset-right)) 10px max(14px,env(safe-area-inset-left));pointer-events:none;transition:opacity .16s ease}
.image-viewer>header button{display:grid;place-items:center;width:42px;height:42px;border:1px solid rgba(255,255,255,.16);border-radius:50%;background:rgba(18,18,20,.68);color:#fff;font-size:21px;pointer-events:auto;backdrop-filter:blur(14px);-webkit-backdrop-filter:blur(14px)}
.image-viewer-viewport{position:absolute;inset:0;overflow:hidden;touch-action:none}
.image-viewer.settling .image-viewer-viewport{transition:transform .2s cubic-bezier(.22,.8,.3,1)}
.image-viewer-strip{display:flex;width:100%;height:100%;will-change:transform}
.image-viewer-strip.animating{transition:transform .24s cubic-bezier(.22,.8,.3,1)}
.image-viewer figure{display:grid;place-items:center;width:100%;height:100%;margin:0;flex:0 0 100%;overflow:hidden}
.image-viewer img{display:block;width:100%;height:100%;max-width:none;max-height:none;object-fit:contain;object-position:center;transform-origin:center;will-change:transform;-webkit-user-drag:none;-webkit-touch-callout:none;user-select:none;pointer-events:auto}
.image-viewer img.animating{transition:transform .22s cubic-bezier(.22,.8,.3,1)}
.image-viewer>footer{position:absolute;z-index:4;inset-inline:0;bottom:0;padding:12px 16px calc(env(safe-area-inset-bottom) + 14px);color:rgba(255,255,255,.82);font-size:12px;font-variant-numeric:tabular-nums;text-align:center;pointer-events:none;text-shadow:0 1px 8px rgba(0,0,0,.72);transition:opacity .16s ease}
.image-viewer-fade-enter-active,.image-viewer-fade-leave-active{transition:opacity .2s ease}
.image-viewer-fade-enter,.image-viewer-fade-leave-to{opacity:0}
@media(prefers-reduced-motion:reduce){.image-viewer,.image-viewer-viewport,.image-viewer-strip.animating,.image-viewer img.animating,.image-viewer-fade-enter-active,.image-viewer-fade-leave-active{transition-duration:.01ms!important}}
</style>
